<template>
  <div v-if="show" class="image-search-overlay" @keydown.esc="handleClose" tabindex="-1">
    <div class="image-search-window" ref="window" :class="{ 'with-dim': showInfo && !acknowledged }">
      <div class="image-search-header">
        <h3>Image Search</h3>
        <button class="close-x" @click="handleClose">×</button>
      </div>

      <div class="image-search-body">
        <div class="uploader">
          <!-- Success view replaces the upload area after send -->
          <div v-if="successVisible" class="upload-success-inner">
            <div class="check-circle-large">✔</div>
            <div class="success-text">Image search request sent
              <div class="small">We will respond as soon as possible</div>
            </div>
            <button class="close-success" @click="closeAfterSuccess">Close</button>
          </div>

          <!-- Normal uploader / preview -->
          <div v-else>
            <div v-if="!previewUrl" class="upload-placeholder">
              <label class="upload-label">
                <input type="file" accept="image/*" @change="onFileChange" />
                <div class="placeholder-box">Click or drop an image here</div>
              </label>
            </div>
            <div v-else class="preview-box">
              <img :src="previewUrl" alt="preview" />
            </div>
          </div>
        </div>

        <div class="actions">
          <button v-if="!successVisible" class="send-btn" :disabled="!file || sending || sent" @click="sendRequest">
            <span v-if="!sending && !sent">Send</span>
            <span v-else-if="sending">Sending...</span>
            <span v-else>Sent</span>
          </button>
          <button v-if="!successVisible" class="cancel-btn" @click="handleClose">Cancel</button>
          <!-- after success, show a single close button inside the success view -->
        </div>
      </div>

      <!-- internal dimmer + centered first-time popup inside the window -->
      <div v-if="showInfo && !acknowledged" class="window-overlay">
        <div class="first-time-popup" role="dialog" aria-modal="true">
          <div class="first-time-content">
            <p>This feature lets you upload an image and request an image-based search. We'll notify you when results are ready.</p>
            <button @click="acknowledge" class="ack-btn">I acknowledge</button>
          </div>
        </div>
      </div>
    </div>

    
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';

export default {
  name: 'ImageSearchModal',
  // import axios and config via module imports so bundler handles them
  // (these will be resolved at build time)
  // NOTE: eslint may warn about unused imports; they are used in sendRequest
  
  props: {
    show: { type: Boolean, default: false }
  },
  data() {
    return {
      file: null,
      previewUrl: '',
      sending: false,
      sent: false,
      successVisible: false,
      showInfo: true,
      acknowledged: false
    };
  },
  watch: {
    show(val) {
      if (val) {
        // when opened, check acknowledgement
        this.acknowledged = localStorage.getItem('imageSearchAcknowledged') === '1';
      } else {
        this.reset();
      }
    }
  },
  methods: {
    onFileChange(e) {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      this.file = f;
      this.previewUrl = URL.createObjectURL(f);
      this.sent = false;
    },
    acknowledge() {
      this.acknowledged = true;
      localStorage.setItem('imageSearchAcknowledged', '1');
    },
    async sendRequest() {
      if (!this.file) return;
      if (!this.$store.getters.isAuthenticated) {
        this.handleClose();
        this.$router.push('/login');
        return;
      }
      this.sending = true;
      try {
        const form = new FormData();
        form.append('image', this.file);
        // Optional: include email if you want to collect it from user later
        // form.append('email', this.email || '');

        // Let axios set the Content-Type (including boundary)
        const resp = await axios.post(`${config.apiBaseUrl}/api/image-search-request`, form, {
          withCredentials: true
        });

        if (resp && resp.data && resp.data.id) {
          this.sent = true;
          this.successVisible = true;
        } else {
          console.error('Unexpected response from server', resp && resp.data);
          alert('Upload succeeded but server did not return an id.');
        }
      } catch (err) {
        console.error('Send failed', err);
        alert('Failed to send image. Please try again.');
      } finally {
        this.sending = false;
      }
    },
    handleClose() {
      this.$emit('close');
    },
    closeAfterSuccess() {
      this.successVisible = false;
      this.handleClose();
    },
    reset() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.file = null;
      this.previewUrl = '';
      this.sending = false;
      this.sent = false;
      this.showInfo = true;
      this.successVisible = false;
    }
  }
};
</script>

<style scoped>
.image-search-overlay{
  position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;z-index:2000;
}
.image-search-window{background:#fff;width:760px;max-width:94%;border-radius:8px;box-shadow:0 10px 30px rgba(0,0,0,0.35);overflow:hidden;position:relative;animation:popIn 220ms ease both}
.image-search-header{display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-bottom:1px solid #eee}
.image-search-header h3{margin:0}
.close-x{background:transparent;border:none;font-size:20px;cursor:pointer}
.image-search-body{padding:20px}
.uploader{display:flex;justify-content:center;margin-bottom:16px}
.upload-placeholder{width:520px;height:360px;border:2px dashed #ddd;display:flex;align-items:center;justify-content:center}
.upload-label input{display:none}
.placeholder-box{color:#777}
.preview-box img{max-width:520px;max-height:360px;display:block}
.actions{display:flex;gap:12px;justify-content:center}
.send-btn{background:#007bff;color:#fff;border:none;padding:10px 18px;border-radius:6px;cursor:pointer}
.send-btn:disabled{opacity:0.6;cursor:not-allowed}
.cancel-btn{background:#eee;border:none;padding:10px 18px;border-radius:6px;cursor:pointer}
.success{color:#fff;background:transparent}

.first-time-popup{background:#fff;border:1px solid #ddd;padding:12px 14px;border-radius:6px;box-shadow:0 10px 30px rgba(0,0,0,0.2);z-index:12;animation:popIn 220ms ease both;max-width:360px;width:88%;text-align:left}
.ack-btn{margin-top:12px;background:#28a745;color:#fff;border:none;padding:8px 12px;border-radius:6px;cursor:pointer}

/* overlay inside the window when first-time popup is visible */
.window-overlay{position:absolute;inset:0;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;z-index:11}

/* small animation */
@keyframes popIn{from{transform:translateY(-8px) scale(.98);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}

/* success full-screen centered card */
/* success UI shown inside the uploader area */
.upload-success-inner{width:520px;height:360px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center}
.check-circle-large{width:86px;height:86px;border-radius:50%;background:#28a745;color:#fff;font-size:44px;display:flex;align-items:center;justify-content:center;margin-bottom:12px}
.success-text{font-size:18px;color:#222;margin-bottom:14px}
.success-text .small{display:block;color:#666;font-size:12px;margin-top:6px}
.close-success{background:#28a745;color:#fff;border:none;padding:10px 16px;border-radius:6px;cursor:pointer}

.image-search-window.with-dim .image-search-body{filter:blur(0.6px);opacity:0.85;transition:opacity 180ms ease}
</style>
